import React, { useMemo, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const DEFAULT_VIEW_OPTIONS = [
  { value: 'week', label: '1 semana', days: 7 },
  { value: 'biweek', label: '2 semanas', days: 14 },
  { value: 'month', label: 'Mensual', days: 42 },
];

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];

const parseIsoDate = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const date = new Date(`${text}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toIso = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const startOfWeekMonday = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const weekDay = d.getDay();
  const diff = weekDay === 0 ? -6 : 1 - weekDay;
  d.setDate(d.getDate() + diff);
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
};

const dateLabel = (date) =>
  date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });

const monthYearLabel = (date) => {
  const raw = date.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
};

/**
 * Calendario reusable para agenda (semanal / bisemanal / mensual).
 * events: [{ date, title, subtitle, status, color }]
 */
const ScheduleCalendar = ({
  events = [],
  isDarkMode = false,
  title = 'Calendario',
  /** Restrict view modes, e.g. ['week','biweek'] */
  allowedViews,
  defaultView = 'week',
  collapsed = false,
  onToggleCollapsed,
  onEventClick,
  onDayClick,
  emptyLabel = 'Sin entregas',
  dayColumnHeight,
  /** Extra controls rendered in the toolbar (filters, toggles, …). */
  toolbarExtra = null,
  /** `planner` applies the Mi Agenda look without changing other calendars. */
  appearance = 'default',
}) => {
  const viewOptions = useMemo(() => {
    if (!Array.isArray(allowedViews) || allowedViews.length === 0) return DEFAULT_VIEW_OPTIONS;
    const allowed = new Set(allowedViews);
    const filtered = DEFAULT_VIEW_OPTIONS.filter((opt) => allowed.has(opt.value));
    return filtered.length ? filtered : DEFAULT_VIEW_OPTIONS;
  }, [allowedViews]);

  const initialView = viewOptions.some((opt) => opt.value === defaultView)
    ? defaultView
    : viewOptions[0]?.value || 'week';

  const [viewMode, setViewMode] = useState(initialView);
  const [anchorDate, setAnchorDate] = useState(() => startOfWeekMonday(new Date()));
  const ui = useUIColors();
  const isPlanner = appearance === 'planner';
  const todayIso = toIso(new Date());
  const plannerAccent = isDarkMode ? '#f2c28b' : '#c45c26';

  const colors = isPlanner
    ? isDarkMode
      ? {
          panel: '#141b23',
          border: 'rgba(242, 226, 198, 0.12)',
          text: '#f6f0e6',
          muted: '#9aa6b2',
          inputBg: '#1b2430',
          chipBg: '#1b2430',
          cellAlt: '#10161d',
          today: 'rgba(242, 194, 139, 0.14)',
        }
      : {
          panel: '#fffaf3',
          border: 'rgba(92, 64, 40, 0.12)',
          text: '#1c1917',
          muted: '#78716c',
          inputBg: '#f7f0e4',
          chipBg: '#f7f0e4',
          cellAlt: '#f3eadc',
          today: 'rgba(196, 92, 38, 0.1)',
        }
    : {
        panel: ui.panel,
        border: ui.border,
        text: ui.text,
        muted: ui.muted,
        inputBg: ui.inputBg,
        chipBg: ui.header,
        cellAlt: ui.section,
        today: isDarkMode ? 'rgba(45, 212, 191, 0.12)' : 'rgba(13, 148, 136, 0.1)',
      };

  const navBtn = {
    height: 32,
    minWidth: 32,
    borderRadius: isPlanner ? 999 : 8,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.text,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    padding: '0 10px',
  };

  const normalizedEvents = useMemo(
    () =>
      (events || [])
        .map((evt, idx) => {
          const date = parseIsoDate(evt.date);
          if (!date) return null;
          return {
            ...evt,
            id: evt.id || `${toIso(date)}-${idx}`,
            dateIso: toIso(date),
            title: evt.title || 'Evento',
            subtitle: evt.subtitle || '',
            status: evt.status || '',
            color: evt.color || '#2563eb',
          };
        })
        .filter(Boolean),
    [events]
  );

  const eventsByDate = useMemo(() => {
    const map = new Map();
    normalizedEvents.forEach((evt) => {
      const list = map.get(evt.dateIso) || [];
      list.push(evt);
      map.set(evt.dateIso, list);
    });
    return map;
  }, [normalizedEvents]);

  const gallonsByDate = useMemo(() => {
    const map = new Map();
    normalizedEvents.forEach((evt) => {
      const gallons = Number(evt.gallons || 0);
      if (!(gallons > 0)) return;
      map.set(evt.dateIso, (map.get(evt.dateIso) || 0) + gallons);
    });
    return map;
  }, [normalizedEvents]);

  const formatDayGallons = (value) => {
    const n = Number(value || 0);
    if (!(n > 0)) return '';
    return `${n.toLocaleString('es-CL', { maximumFractionDigits: 1 })} gal`;
  };

  const currentViewMeta = viewOptions.find((opt) => opt.value === viewMode) || viewOptions[0];

  const gridDays = useMemo(() => {
    if (viewMode === 'month') {
      const monthStart = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
      const start = startOfWeekMonday(monthStart);
      return Array.from({ length: 42 }, (_, i) => addDays(start, i));
    }
    return Array.from({ length: currentViewMeta.days }, (_, i) => addDays(anchorDate, i));
  }, [viewMode, anchorDate, currentViewMeta.days]);

  const periodLabel = useMemo(() => {
    if (!gridDays.length) return '';
    if (viewMode === 'month') return monthYearLabel(anchorDate);
    const from = gridDays[0];
    const to = gridDays[gridDays.length - 1];
    return `${dateLabel(from)} - ${dateLabel(to)}`;
  }, [viewMode, gridDays, anchorDate]);

  const handlePrev = () => {
    if (viewMode === 'month') {
      const prevMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 1, 1);
      setAnchorDate(startOfWeekMonday(prevMonth));
      return;
    }
    setAnchorDate((prev) => addDays(prev, -currentViewMeta.days));
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      const nextMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 1);
      setAnchorDate(startOfWeekMonday(nextMonth));
      return;
    }
    setAnchorDate((prev) => addDays(prev, currentViewMeta.days));
  };

  const handleToday = () => {
    const today = new Date();
    if (viewMode === 'month') {
      setAnchorDate(startOfWeekMonday(new Date(today.getFullYear(), today.getMonth(), 1)));
    } else {
      setAnchorDate(startOfWeekMonday(today));
    }
  };

  const columns = viewMode === 'month' ? 7 : Math.min(currentViewMeta.days, 7);
  const canCollapse = typeof onToggleCollapsed === 'function';
  const resolvedDayColumnHeight = dayColumnHeight ?? (viewMode === 'month' ? 128 : 300);

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: isPlanner ? 22 : 12,
        background: colors.panel,
        overflow: 'hidden',
        margin: isPlanner ? 0 : '10px 0 12px',
        boxShadow: isPlanner ? (isDarkMode ? '0 18px 40px rgba(0,0,0,0.28)' : '0 18px 40px rgba(92, 64, 40, 0.08)') : 'none',
      }}
    >
      <div
        style={{
          padding: isPlanner ? '16px 18px' : '10px 14px',
          borderBottom: collapsed ? 'none' : `1px solid ${colors.border}`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: isPlanner ? 'transparent' : colors.chipBg,
        }}
      >
        <div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: colors.text,
              fontWeight: 700,
              fontFamily: isPlanner ? 'Iowan Old Style, Palatino Linotype, Palatino, Georgia, serif' : 'inherit',
              fontSize: isPlanner ? 18 : 14,
            }}
          >
            <CalendarDays size={16} color={isPlanner ? plannerAccent : colors.text} />
            {title}
          </div>
          {!collapsed ? (
            <div style={{ marginTop: '4px', fontSize: '12px', color: colors.muted, letterSpacing: isPlanner ? '0.04em' : 0 }}>
              {periodLabel}
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {!collapsed ? (
            <>
              {toolbarExtra}
              <select
                value={viewMode}
                onChange={(e) => {
                  const nextMode = e.target.value;
                  setViewMode(nextMode);
                  if (nextMode === 'month') {
                    const base = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
                    setAnchorDate(startOfWeekMonday(base));
                  } else {
                    setAnchorDate(startOfWeekMonday(anchorDate));
                  }
                }}
                style={{
                  height: '32px',
                  borderRadius: isPlanner ? 999 : 8,
                  border: `1px solid ${colors.border}`,
                  background: colors.inputBg,
                  color: colors.text,
                  padding: '0 10px',
                }}
              >
                {viewOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button type="button" onClick={handlePrev} style={{ ...navBtn, padding: 0, width: 32 }}>
                <ChevronLeft size={16} />
              </button>
              <button type="button" onClick={handleToday} style={navBtn}>
                Hoy
              </button>
              <button type="button" onClick={handleNext} style={{ ...navBtn, padding: 0, width: 32 }}>
                <ChevronRight size={16} />
              </button>
            </>
          ) : null}
          {canCollapse ? (
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-expanded={!collapsed}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                height: '28px',
                padding: '0 8px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                background: colors.panel,
                color: colors.text,
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                lineHeight: 1.2,
              }}
            >
              {collapsed ? 'Mostrar' : 'Ocultar'}
              {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </button>
          ) : null}
        </div>
      </div>

      {!collapsed ? (
        <div style={{ overflowX: 'auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, minmax(180px, 1fr))`,
            }}
          >
            {Array.from({ length: columns }, (_, i) => (
              <div
                key={`weekday-${i}`}
                style={{
                  padding: isPlanner ? '10px 10px 8px' : '8px 10px',
                  borderBottom: `1px solid ${colors.border}`,
                  borderRight: i < columns - 1 ? `1px solid ${colors.border}` : 'none',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: colors.muted,
                  textTransform: 'uppercase',
                  letterSpacing: isPlanner ? '0.12em' : '0.05em',
                  background: colors.cellAlt,
                }}
              >
                {WEEKDAY_LABELS[i]}
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, minmax(180px, 1fr))`,
            }}
          >
            {gridDays.map((day, idx) => {
              const iso = toIso(day);
              const dayEvents = eventsByDate.get(iso) || [];
              const dayGallonsLabel = formatDayGallons(gallonsByDate.get(iso) || 0);
              const isCurrentMonth = day.getMonth() === new Date(anchorDate).getMonth();
              const isToday = iso === todayIso;
              return (
                <div
                  key={`${iso}-${idx}`}
                  role={onDayClick ? 'button' : undefined}
                  tabIndex={onDayClick ? 0 : undefined}
                  onClick={onDayClick ? () => onDayClick(iso) : undefined}
                  onKeyDown={
                    onDayClick
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onDayClick(iso);
                          }
                        }
                      : undefined
                  }
                  style={{
                    height: resolvedDayColumnHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: isPlanner ? '10px 8px' : '8px',
                    borderRight: (idx + 1) % columns !== 0 ? `1px solid ${colors.border}` : 'none',
                    borderBottom: `1px solid ${colors.border}`,
                    background:
                      isToday
                        ? colors.today
                        : viewMode === 'month' && !isCurrentMonth
                          ? colors.cellAlt
                          : 'transparent',
                    boxSizing: 'border-box',
                    cursor: onDayClick ? 'pointer' : 'default',
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      color: colors.text,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}
                  >
                    {isPlanner ? (
                      <span
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isToday ? plannerAccent : 'transparent',
                          color: isToday ? (isDarkMode ? '#1c1917' : '#fffaf3') : colors.text,
                          fontFamily: 'Iowan Old Style, Palatino Linotype, Palatino, Georgia, serif',
                          fontSize: 15,
                        }}
                      >
                        {day.getDate()}
                      </span>
                    ) : (
                      <span>{dateLabel(day)}</span>
                    )}
                    {dayGallonsLabel ? (
                      <span style={{ fontSize: '11px', fontWeight: 700, color: colors.muted, whiteSpace: 'nowrap' }}>
                        {dayGallonsLabel}
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      marginTop: '6px',
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      paddingRight: '2px',
                    }}
                  >
                    {dayEvents.length === 0 ? (
                      <span style={{ fontSize: '11px', color: colors.muted }}>{emptyLabel}</span>
                    ) : (
                      dayEvents.map((evt) => (
                        <div
                          key={evt.id}
                          role={onEventClick ? 'button' : undefined}
                          tabIndex={onEventClick ? 0 : undefined}
                          onClick={
                            onEventClick
                              ? (event) => {
                                  event.stopPropagation();
                                  onEventClick(evt);
                                }
                              : undefined
                          }
                          onKeyDown={
                            onEventClick
                              ? (event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onEventClick(evt);
                                  }
                                }
                              : undefined
                          }
                          style={{
                            borderLeft: `3px solid ${evt.color}`,
                            background: isPlanner ? `${evt.color}22` : colors.chipBg,
                            borderRadius: isPlanner ? 10 : 6,
                            padding: isPlanner ? '7px 8px' : '6px',
                            cursor: onEventClick ? 'pointer' : 'default',
                            flexShrink: 0,
                          }}
                        >
                          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.text }}>{evt.title}</div>
                          {evt.subtitle ? (
                            <div style={{ fontSize: '10px', color: colors.muted, marginTop: '2px' }}>{evt.subtitle}</div>
                          ) : null}
                          {evt.status ? (
                            <div style={{ fontSize: '10px', color: colors.muted, marginTop: '2px' }}>{evt.status}</div>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ScheduleCalendar;
